import React, { useMemo, useState } from 'react';
import {
  TalentMatchingTabContextProvider,
  type TalentMatchingTabContextValue,
} from 'talent_matching/contexts/talent_matching_tab_context';
import FilterForm from 'talent_matching/components/filter_form';
import { IndexingStatusProvider } from 'talent_matching/contexts/indexing_status_context';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useLocalStorage } from 'react-use';
import { TalentMatchingFilters } from 'talent_matching/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { extractFormFieldsFromSavedSearch } from 'talent_matching/components/filters/utils';
import type { SavedSearch } from 'talent_search/shared/types';

export type InitialSavedFilterSet = {
  id: number;
  label: string;
  value: SavedSearch;
};

type Props = Omit<
  TalentMatchingTabContextValue,
  | 'activeSidePanelTab'
  | 'setActiveSidePanelTab'
  | 'setSavedSearchOptions'
  | 'savedFilterSet'
  | 'setSavedFilterSet'
> & {
  initialFilters: Partial<TalentMatchingFilters>;
  initialSavedFilterSet: InitialSavedFilterSet | null;
  pollIndexingOnLoad: boolean;
};

const TalentMatching = (talentMatchingProps: Props) => {
  const {
    initialFilters,
    initialSavedFilterSet,
    pollIndexingOnLoad,
    savedSearchOptions: initialSavedSearchOptions,
    ...stableContextProps
  } = talentMatchingProps;

  const [activeSidePanelTab, setActiveSidePanelTab] = useLocalStorage<
    string | null
  >('talent_matching_candidate_panel_tab', null);

  const [savedSearchOptions, setSavedSearchOptions] = React.useState(
    initialSavedSearchOptions
  );

  const [savedFilterSet, setSavedFilterSet] = useState<Item | null>(() =>
    initialSavedFilterSet
      ? {
          label: initialSavedFilterSet.label,
          value: initialSavedFilterSet.id.toString(),
        }
      : null
  );

  const seededInitialFilters = useMemo<Partial<TalentMatchingFilters>>(() => {
    if (!initialSavedFilterSet) {
      return initialFilters;
    }

    const fromSavedSearch = extractFormFieldsFromSavedSearch(
      initialSavedFilterSet.value
    ) as unknown as Partial<TalentMatchingFilters>;
    return { ...initialFilters, ...fromSavedSearch };
  }, [initialFilters, initialSavedFilterSet]);

  const contextValue = useMemo(
    () => ({
      ...stableContextProps,
      activeSidePanelTab: activeSidePanelTab ?? null,
      setActiveSidePanelTab,
      savedSearchOptions,
      setSavedSearchOptions,
      savedFilterSet,
      setSavedFilterSet,
    }),
    [
      stableContextProps,
      activeSidePanelTab,
      setActiveSidePanelTab,
      savedSearchOptions,
      savedFilterSet,
    ]
  );

  return (
    <TalentMatchingTabContextProvider value={contextValue}>
      <IndexingStatusProvider pollOnLoad={pollIndexingOnLoad}>
        <FilterForm initialFilters={seededInitialFilters} />
        <ReactQueryDevtools initialIsOpen={false} />
      </IndexingStatusProvider>
    </TalentMatchingTabContextProvider>
  );
};

export default TalentMatching;
